import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { finalize } from 'rxjs';
import { ToastConfig } from '@app/shared';
import { OpportunityService } from '../../../../core/services/opportunity.service';
import { SaleOrderPdfVersion } from '../../../../shared/models/sale-order-pdf-version';
import {
  buildArchiveVersionConfirmation,
  buildVersionFileName,
  formatVersionArchivedMessage,
  formatVersionExpiration,
  formatVersionIssuedOn,
  formatVersionLabel,
  formatVersionConversionDate,
  formatVersionCurrency,
  formatVersionRate,
  hasNoVersions,
} from './budget-pdf-versions.utils';

@Component({
  selector: 'app-budget-pdf-versions',
  templateUrl: './budget-pdf-versions.component.html',
  styleUrl: './budget-pdf-versions.component.less',
  standalone: false,
})
export class BudgetPdfVersionsComponent implements OnChanges {
  @Input() saleOrderId: number | null = null;
  /** Bumped by the parent after a successful generation to force a reload. */
  @Input() refreshKey = 0;
  @Input() budgetName = '';
  /** ISO of the budget currency, shown for legacy or non-converted versions. */
  @Input() budgetCurrencyCode: string | null = null;

  versions: SaleOrderPdfVersion[] = [];
  loading = false;
  loadFailed = false;
  downloadingId: number | null = null;
  archivingId: number | null = null;

  @ViewChild('notificationTmpl', { static: true }) notificationTmpl!: TemplateRef<any>;
  toastConfig: ToastConfig = { icon: '', iconColor: '', content: '', showUndo: false };
  notRef: any;

  readonly formatLabel = formatVersionLabel;
  readonly formatIssuedOn = formatVersionIssuedOn;
  readonly formatExpiration = formatVersionExpiration;
  readonly formatRate = formatVersionRate;
  readonly formatConversionDate = formatVersionConversionDate;

  formatCurrency(version: SaleOrderPdfVersion): string {
    return formatVersionCurrency(version, this.budgetCurrencyCode);
  }

  constructor(
    private opportunityService: OpportunityService,
    private notificationService: NzNotificationService,
    private cdr: ChangeDetectorRef,
    private modalService: NzModalService
  ) {}

  get isEmpty(): boolean {
    return !this.loading && !this.loadFailed && hasNoVersions(this.versions);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['saleOrderId'] || changes['refreshKey']) {
      this.loadVersions();
    }
  }

  loadVersions(): void {
    if (!this.saleOrderId) {
      this.versions = [];
      return;
    }

    this.loading = true;
    this.loadFailed = false;
    this.opportunityService
      .getSaleOrderPdfVersions(this.saleOrderId)
      .pipe(finalize(() => this.settle(() => (this.loading = false))))
      .subscribe({
        next: response => (this.versions = response?.data ?? []),
        error: () => {
          this.loadFailed = true;
          this.versions = [];
        },
      });
  }

  downloadVersion(version: SaleOrderPdfVersion): void {
    if (this.downloadingId !== null) {
      return;
    }

    this.downloadingId = version.id;
    this.opportunityService
      .downloadSaleOrderPdfVersion(version.id)
      .pipe(finalize(() => this.settle(() => (this.downloadingId = null))))
      .subscribe({
        next: blob => this.saveBlob(blob, buildVersionFileName(this.budgetName, version.versionNumber)),
        error: error =>
          this.showToast(
            'error',
            error?.status === 404
              ? 'No se pudo descargar la versión: el archivo no está disponible.'
              : 'No se pudo descargar la versión. Inténtalo de nuevo o avisa a soporte.'
          ),
      });
  }

  /** "Eliminar" archives the version after an explicit confirmation; cancelling sends nothing. */
  confirmArchiveVersion(version: SaleOrderPdfVersion): void {
    if (this.archivingId !== null) {
      return;
    }

    const confirmation = buildArchiveVersionConfirmation(version.versionNumber);
    this.modalService.confirm({
      nzTitle: confirmation.title,
      nzContent: confirmation.content,
      nzOkText: confirmation.okText,
      nzOkDanger: true,
      nzCancelText: confirmation.cancelText,
      nzOnOk: () => this.archiveVersion(version),
    });
  }

  private archiveVersion(version: SaleOrderPdfVersion): void {
    if (this.archivingId !== null) {
      return;
    }

    this.archivingId = version.id;
    this.opportunityService
      .archiveSaleOrderPdfVersion(version.id)
      .pipe(finalize(() => this.settle(() => (this.archivingId = null))))
      .subscribe({
        next: () => {
          this.showToast('success', formatVersionArchivedMessage(version.versionNumber));
          this.loadVersions();
        },
        error: () =>
          this.showToast(
            'error',
            `No se pudo eliminar la v${version.versionNumber}. Inténtalo de nuevo o avisa a soporte.`
          ),
      });
  }

  /** Same toast as the rest of the app (app-toast, bottom right), not the default ng-zorro one. */
  private showToast(kind: 'success' | 'error', content: string): void {
    this.toastConfig =
      kind === 'success'
        ? { icon: 'check_circle', iconColor: '#6CDA75', content, showUndo: false }
        : { icon: 'dangerous', iconColor: '#CB2A2F', content, showUndo: false };
    this.notRef = this.notificationService.create('blank', '', this.notificationTmpl, {
      nzPlacement: 'bottomRight',
    });
  }

  /**
   * The drawers live inside nz-drawer, which is OnPush: an HTTP response alone does not re-render
   * this view, so every async state change must mark it for check.
   */
  private settle(update: () => void): void {
    update();
    this.cdr.markForCheck();
  }

  protected saveBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }
}
